import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import ConfirmationDialog from './ConfirmationDialog';

function RevisionHistoryModal({ isOpen, onClose, noteName, onRestore }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingRevision, setViewingRevision] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoringRevision, setRestoringRevision] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRevision, setDeletingRevision] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen && noteName) {
      loadRevisions();
    } else {
      // Reset state when modal closes
      setRevisions([]);
      setError(null);
      setViewingRevision(null);
      setShowRestoreConfirm(false);
      setRestoringRevision(null);
      setShowDeleteConfirm(false);
      setDeletingRevision(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadRevisions runs when modal opens
  }, [isOpen, noteName]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadRevisions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/notes/${noteName}/revisions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRevisions(response.data.data || []);
      } else {
        setError('Failed to load revisions');
      }
    } catch (err) {
      console.error('Error loading revisions:', err);
      setError(err.response?.data?.error || 'Failed to load revisions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRevision = async (timestamp) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/notes/${noteName}/revisions/${timestamp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setViewingRevision(response.data.data);
      }
    } catch (err) {
      console.error('Error loading revision content:', err);
      setError(err.response?.data?.error || 'Failed to load revision content');
    }
  };

  const handleRestoreClick = (revision) => {
    setRestoringRevision(revision);
    setShowRestoreConfirm(true);
  };

  const handleDeleteClick = (revision) => {
    setDeletingRevision(revision);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRevision) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.delete(
        `${API_BASE_URL}/admin/notes/${noteName}/revisions/${deletingRevision.timestamp}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setShowDeleteConfirm(false);
        setDeletingRevision(null);
        
        // If we're viewing the deleted revision, clear it
        if (viewingRevision && viewingRevision.timestamp === deletingRevision.timestamp) {
          setViewingRevision(null);
        }
        
        // Reload revisions
        await loadRevisions();
      }
    } catch (err) {
      console.error('Error deleting revision:', err);
      setError(err.response?.data?.error || 'Failed to delete revision');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoringRevision) return;
    
    setRestoring(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post(
        `${API_BASE_URL}/admin/notes/${noteName}/revisions/${restoringRevision.timestamp}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setShowRestoreConfirm(false);
        setRestoringRevision(null);
        setViewingRevision(null);
        
        // Reload revisions to show the new revision (current saved before restore)
        await loadRevisions();
        
        // Notify parent to refresh note data
        if (onRestore) {
          onRestore(response.data.data);
        }
        
        onClose();
      }
    } catch (err) {
      console.error('Error restoring revision:', err);
      setError(err.response?.data?.error || 'Failed to restore revision');
    } finally {
      setRestoring(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col animate-in fade-in duration-200">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Revision History</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading revisions...</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {!loading && !error && revisions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No revisions found. Revisions are created automatically when you save changes to the note.
              </div>
            )}

            {!loading && !error && revisions.length > 0 && (
              <div className="space-y-2">
                {revisions.map((revision) => (
                  <div
                    key={revision.timestamp}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">
                          {revision.date}
                        </div>
                        {revision.preview && (
                          <div className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {revision.preview}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleViewRevision(revision.timestamp)}
                          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleRestoreClick(revision)}
                          className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handleDeleteClick(revision)}
                          className="px-3 py-1 text-sm text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* View Revision Content */}
            {viewingRevision && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-900">Revision Content</h4>
                  <button
                    onClick={() => setViewingRevision(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-4 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                    {viewingRevision.content}
                  </pre>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => handleDeleteClick(viewingRevision)}
                    className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                  >
                    Delete This Revision
                  </button>
                  <button
                    onClick={() => handleRestoreClick(viewingRevision)}
                    className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                  >
                    Restore This Version
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showRestoreConfirm}
        title="Restore Revision"
        message={`Are you sure you want to restore this revision from ${restoringRevision?.date}? The current version will be saved as a new revision first, so you can undo this action.`}
        confirmText="Restore"
        cancelText="Cancel"
        onConfirm={handleRestoreConfirm}
        onCancel={() => {
          setShowRestoreConfirm(false);
          setRestoringRevision(null);
        }}
        disabled={restoring}
        confirmButtonClass="bg-blue-600 hover:bg-blue-700"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Revision"
        message={`Are you sure you want to delete this revision from ${deletingRevision?.date}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeletingRevision(null);
        }}
        disabled={deleting}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}

export default RevisionHistoryModal;
